import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../../../authContext/context';
import { postRequest, getRequest, putRequest } from '../../../lib/api';
import Swal from 'sweetalert2';
import BreadcrumbsWrapper from '../../../components/breadcrumbs';
import {
  Box,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack
} from '@mui/material';
import { Form, Row, Col } from 'react-bootstrap';
import CircularProgress from '@mui/material/CircularProgress';

export default function EditSMSTemplate() {
  const navigate = useNavigate();
  const { id } = useParams();
  const authCtx = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    productId: '',
    message: ''
  });

  // Fetch template data and products on component mount
  useEffect(() => {
    fetchProducts();
    if (id) {
      fetchTemplateData();
    }
  }, [id]);

  const fetchProducts = async () => {
    try {
      const response = await postRequest('private/getproductList', {}, authCtx.token);
      if (response?.data) {
        setProducts(response.data);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchTemplateData = async () => {
    setLoading(true);
    try {
      const response = await getRequest(`private/get-sms-template/${id}`, authCtx.token);

      if (response?.status === 'success' || response?.status === 200) {
        const template = response.data;
        setFormData({
          name: template.name || '',
          productId: template.productId?._id || template.productId || '',
          message: template.message || ''
        });
      } else {
        throw new Error('Failed to fetch template data');
      }
    } catch (error) {
      console.error('Error fetching template:', error);
      Swal.fire('Error', 'Failed to load SMS template', 'error');
      navigate('/sms-templates');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      Swal.fire('Error', 'Please enter template name', 'error');
      return;
    }

    if (!formData.productId) {
      Swal.fire('Error', 'Please select a product', 'error');
      return;
    }

    if (!formData.message.trim()) {
      Swal.fire('Error', 'Please enter message', 'error');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        productId: formData.productId,
        message: formData.message,
        updated_id: localStorage.getItem('id') || authCtx.user?.id || ''
      };

      const response = await putRequest(`private/update-sms-template/${id}`, payload, authCtx.token);

      if (response?.status === 'success' || response?.status === 200) {
        Swal.fire('Success', 'SMS Template updated successfully!', 'success');
        navigate('/sms-templates');
      } else {
        throw new Error(response?.message || 'Failed to update SMS template');
      }
    } catch (error) {
      console.error('Error updating SMS template:', error);
      Swal.fire('Error', error.message || 'Failed to update SMS template', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleViewMergeFields = () => {
    Swal.fire({
      title: 'Merge Fields',
      html: `
        <div style="text-align: left; padding: 20px;">
          <h5>Available Merge Fields:</h5>
          <ul>
            <li><strong>{{customer_name}}</strong> - Customer's full name</li>
            <li><strong>{{customer_forename}}</strong> - Customer's first name</li>
            <li><strong>{{customer_surname}}</strong> - Customer's last name</li>
            <li><strong>{{customer_email}}</strong> - Customer's email</li>
            <li><strong>{{customer_phone}}</strong> - Customer's phone number</li>
            <li><strong>{{lead_id}}</strong> - Lead reference ID</li>
            <li><strong>{{product_name}}</strong> - Product name</li>
            <li><strong>{{company_name}}</strong> - Company name</li>
          </ul>
        </div>
      `,
      icon: 'info',
      width: 600
    });
  };

  if (loading) {
    return (
      <>
        <BreadcrumbsWrapper
          items={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'SMS Templates', path: '/sms-templates' },
            { label: 'Edit SMS Template', path: '' },
          ]}
        />
        <br />
        <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
          <CircularProgress />
        </div>
      </>
    );
  }

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'SMS Templates', path: '/sms-templates' },
          { label: 'Edit SMS Template', path: '' },
        ]}
      />
      <br />

      <div className='page-header pb-3'>
        <h1 className='mt-0 mb-0'>Edit SMS Template</h1>
      </div>

      <div className='page-top-formview mb-3'>
        <Form onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="name">
              <FormControl sx={{ my: 1, width: '100%' }} variant="outlined">
                <TextField
                  label="Name"
                  variant="outlined"
                  value={formData.name}
                  onChange={handleChange('name')}
                  placeholder="Enter template name"
                  required
                />
              </FormControl>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} controlId="productId">
              <FormControl sx={{ my: 1, width: '100%' }} variant="outlined">
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ flexGrow: 1 }}>
                    <InputLabel id="product-label">Product</InputLabel>
                    <Select
                      labelId="product-label"
                      id="product"
                      value={formData.productId}
                      label="Product"
                      onChange={handleChange('productId')}
                      fullWidth
                      required
                    >
                      <MenuItem value="">Select Product</MenuItem>
                      <MenuItem value="any">Any</MenuItem>
                      {products.map((product) => (
                        <MenuItem key={product._id} value={product._id}>
                          {product.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                  <Button
                    variant="outlined"
                    onClick={handleViewMergeFields}
                    sx={{
                      borderColor: '#17a2b8',
                      color: '#17a2b8',
                      '&:hover': {
                        backgroundColor: '#e7f6f8',
                        borderColor: '#17a2b8'
                      }
                    }}
                  >
                    View Merge Fields
                  </Button>
                </Stack>
              </FormControl>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} controlId="message">
              <FormControl sx={{ my: 1, width: '100%' }} variant="outlined">
                <TextField
                  label="Message"
                  variant="outlined"
                  value={formData.message}
                  onChange={handleChange('message')}
                  placeholder="Enter SMS message"
                  multiline
                  rows={6}
                  required
                />
              </FormControl>
            </Form.Group>
          </Row>

          <Row>
            <Form.Group as={Col}>
              <FormControl sx={{ my: 1, width: '100%' }} variant="outlined">
                <Stack spacing={2} direction="row">
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/sms-templates')}
                    sx={{
                      borderColor: '#6c757d',
                      color: '#6c757d',
                      '&:hover': {
                        backgroundColor: '#f8f9fa',
                        borderColor: '#6c757d'
                      }
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={loading}
                    sx={{
                      backgroundColor: '#bb0000',
                      '&:hover': {
                        backgroundColor: '#990000'
                      }
                    }}
                  >
                    {loading ? <CircularProgress size={20} color="inherit" /> : 'Update'}
                  </Button>
                </Stack>
              </FormControl>
            </Form.Group>
          </Row>
        </Form>
      </div>
    </>
  );
}
